import { StatusOptionType, StatusType } from '@oy/types';
import { PillColor, TablePill } from '@oy/uidesktop';
import { useTranslate } from '.';
const prefix = 'common_status_';

/**
 * [DYNAMIC TRANSLATION] This hooks will dynamically
 * translate product type response from BE
 *
 * @remark
 * Oy product in primarily on Account Statement
 *
 * @example
 * BULK_DISBURSEMENT, API_DISBURSEMENT, etc
 */
export const useStatusMap = () => {
  const { getTranslation } = useTranslate();

  /**
   * mapStatus is helper to dynamically translate status registered on StatusType
   * If it is not registered, it will help you remove the underscore ;)
   *
   * @param status - unregistered status can be registered in {@link src/models/StatusType}
   * @returns The dynamic translation of a status
   */
  const mapStatus = (status: string | StatusType) => {
    if (status == '') return '';

    const productTitle = getTranslation(`${prefix}${status.toLowerCase()}`);

    if (productTitle.includes('_')) {
      return status.replace(/_/g, ' ');
    }
    return productTitle;
  };

  /**
   * this will help you to get your list of status have dynamic translation
   *
   * @param statuses - an Array of StatusType
   * @returns an alphabetically ordered statuses with the capability of dynamic translation
   */
  const getStatuses = (
    statuses: StatusType[],
    defaultAllStatus?: string,
  ): StatusOptionType[] => {
    const mappedStatus = statuses.map(e => ({
      value: e,
      label: mapStatus(e),
    }));
    return [
      {
        value: '',
        label: defaultAllStatus ?? getTranslation(`common_allStatus`),
      },
      ...mappedStatus.sort((obj1, obj2) => {
        return obj1.label > obj2.label ? 1 : -1;
      }),
    ];
  };

  /**
   * Only help you define the color of the status
   *
   * @param status - You should add your status on {@link src/models/StatusType}
   * @returns The color of a status
   *
   * @remark You can see {@link src/constants/Status} for array reference
   */
  const mapPillThemeByStatus = (status: StatusType): PillColor => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
      case 'APPROVED':
      case 'COMPLETE':
      case 'DELIVERED':
      case 'DISBURSEMENT_SUCCESSFUL':
      case 'FINISH':
      case 'CONNECTED':
      case 'PAID':
      case 'REFUNDED':
      case 'SUCCESS':
      case 'LINKED':
      case 'REIMBURSEMENT_APPROVED':
      case 'REIMBURSEMENT_COMPLETED':
        return PillColor.Green;

      case 'AUTO_FILLING':
      case 'INSUFFICIENT_BALANCE':
      case 'NEED_REVIEW':
      case 'OPEN_FOR_PAYMENT':
      case 'OVERDUE':
      case 'PENDING_APPROVAL':
      case 'REIMBURSEMENT_NEED_APPROVAL':
      case 'SCHEDULED_':
      case 'SCHEDULED_PAYMENT':
      case 'UNCONFIRMED':
      case 'WAITING_APPROVAL':
      case 'WAITING_CONFIRMATION':
      case 'WAITING_DEPOSIT':
      case 'WAITING_WITHDRAWAL':
      case 'WAITING_EXECUTION':
      case 'WAITING_INPUT':
      case 'WAITING_PAYMENT':
      case 'WAITING':
        return PillColor.Purple;

      case 'CHARGE_IN_PROGRESS':
      case 'CHARGE_SUCCESS':
      case 'DISBURSE_IN_PROGRESS':
      case 'DEPOSIT_RECEIVED':
      case 'TOP_UP_SUCCESSFUL':
      case 'IN_PROGRESS':
      case 'INQUIRED':
      case 'PARTIALLY_PAID':
      case 'PAYMENT_DETECTED':
      case 'PAYMENT_IN_PROGRESS':
      case 'PENDING_FORCE_CREDIT':
      case 'PENDING':
      case 'PROCESS':
      case 'PROCESSED':
      case 'RUNNING':
      case 'SUSPECT_FORCE_CREDIT':
      case 'SUSPECT':
      case 'NEED_TOPUP':
      case 'FAILED_RENEWAL':
      case 'PENDING_DISBURSEMENT':
        return PillColor.Yellow;

      case 'BALANCE_IS_NOT_ENOUGH':
      case 'BANK_ACCOUNT_NOT_FOUND':
      case 'CANCEL':
      case 'CANCELED':
      case 'CANCELLED':
      case 'DEACTIVATED':
      case 'DECLINED':
      case 'DISBURSEMENT_FAILED':
      case 'EXPIRED_VA':
      case 'EXPIRED':
      case 'FAILED_FORCE_CREDIT':
      case 'FAILED':
      case 'NEED_REVISION':
      case 'NONACTIVE':
      case 'PAYMENT_FAILED':
      case 'REJECTED':
      case 'STATIC_TRX_EXPIRED':
      case 'TEMPORARILY_SUSPEND':
      case 'TRANSACTION_FAILED':
      case 'UNPAID':
        return PillColor.Red;

      default:
        return PillColor.Grey;
    }
  };

  /**
   * Only help you define the color of the status
   *
   * @param status - You should add your status on {@link src/models/StatusType}
   * @returns The color of a status
   *
   * @remark You can see {@link src/constants/Status} for array reference
   */
  const mapPillColorByStatus = (status: StatusType) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
      case 'APPROVED':
      case 'COMPLETE':
      case 'DELIVERED':
      case 'DISBURSEMENT_SUCCESSFUL':
      case 'FINISH':
      case 'PAID':
      case 'REFUNDED':
      case 'SUCCESS':
      case 'LINKED':
      case 'REIMBURSEMENT_APPROVED':
      case 'REIMBURSEMENT_COMPLETED':
        return TablePill.Color.Green;

      case 'INSUFFICIENT_BALANCE':
      case 'OPEN_FOR_PAYMENT':
      case 'OVERDUE':
      case 'PENDING_APPROVAL':
      case 'REIMBURSEMENT_NEED_APPROVAL':
      case 'RESUBMITTED':
      case 'SCHEDULED_':
      case 'SCHEDULED_PAYMENT':
      case 'SUBMITTED':
      case 'WAITING_APPROVAL':
      case 'WAITING_CONFIRMATION':
      case 'WAITING_DEPOSIT':
      case 'WAITING_EXECUTION':
      case 'WAITING_GENERATE_VA':
      case 'WAITING_INPUT':
      case 'WAITING_PAYMENT':
      case 'WAITING_WITHDRAWAL':
      case 'WAITING':
        return TablePill.Color.Purple;

      case 'CHARGE_IN_PROGRESS':
      case 'CHARGE_SUCCESS':
      case 'DISBURSE_IN_PROGRESS':
      case 'DEPOSIT_RECEIVED':
      case 'TOP_UP_SUCCESSFUL':
      case 'IN_PROGRESS':
      case 'INQUIRED':
      case 'PARTIALLY_PAID':
      case 'PAYMENT_DETECTED':
      case 'PAYMENT_IN_PROGRESS':
      case 'PENDING_FORCE_CREDIT':
      case 'PENDING':
      case 'PROCESS':
      case 'PROCESSED':
      case 'RUNNING':
      case 'SUSPECT_FORCE_CREDIT':
      case 'SUSPECT':
      case 'NEED_TOPUP':
      case 'FAILED_RENEWAL':
      case 'PENDING_PAYMENT':
        return TablePill.Color.Yellow;

      case 'BALANCE_IS_NOT_ENOUGH':
      case 'BANK_ACCOUNT_NOT_FOUND':
      case 'CANCEL':
      case 'CANCELED':
      case 'CANCELLED':
      case 'DEACTIVATED':
      case 'DECLINED':
      case 'DISBURSEMENT_FAILED':
      case 'EXPIRED_VA':
      case 'EXPIRED':
      case 'FAILED_FORCE_CREDIT':
      case 'FAILED':
      case 'NEED_REVISION':
      case 'NONACTIVE':
      case 'PAYMENT_FAILED':
      case 'REJECTED':
      case 'STATIC_TRX_EXPIRED':
      case 'TEMPORARILY_SUSPEND':
      case 'TRANSACTION_FAILED':
      case 'UNPAID':
      case 'VOID':
        return TablePill.Color.Red;

      default:
        return TablePill.Color.Gray;
    }
  };

  /**
   * If you want to use this for Pill, you can use <StatusPill/> component instead.
   *
   * @param status - You should add your status on {@link src/models/StatusType}
   * @returns The color and bilingual text version of a status
   */
  const getPillThemeByStatus = (status: StatusType) => {
    return {
      color: mapPillThemeByStatus(status),
      text: mapStatus(status).toUpperCase(),
    };
  };

  /**
   * If you want to use this for Pill, you can use <StatusPill/> component instead.
   *
   * @param status - You should add your status on {@link src/models/StatusType}
   * @returns The color and bilingual text version of a status
   */
  const getPillColorByStatus = (status: StatusType) => {
    return {
      color: mapPillColorByStatus(status),
      text: mapStatus(status).toUpperCase(),
    };
  };

  return { getStatuses, mapStatus, getPillThemeByStatus, getPillColorByStatus };
};
